import { FC, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Button,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@capacit-com/lara-law-shared-ui";
import usePiiWhitelist from "@/hooks/usePiiWhitelist";
import PageContent from "@/pages/PageContent";
import { logger } from "@/lib/logger";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";

/**
 * Admin settings for the per-tenant PII whitelist (LAR-27 #5). Approved words
 * are never flagged as personal data in chat — but only for soft matches
 * (names/organisations); structured identifiers (CPR, phone, e-mail, account)
 * are always flagged regardless of this list, and the sender decides per
 * occurrence in the review dialog (audited server-side). A term of that kind is
 * accepted here rather than rejected, and the helptext says so (LAR-367 #3).
 *
 * Gated by the ManagePiiSettings permission at the route (see AppRouter).
 */
const PiiWhitelistSettingsPage: FC = () => {
  const { useQueryWhitelist, addTerm, removeTerm } = usePiiWhitelist();
  const whitelistQuery = useQueryWhitelist();
  const [newTerm, setNewTerm] = useState("");

  const terms = whitelistQuery.data?.terms ?? [];

  const handleAdd = async () => {
    const value = newTerm.trim();
    if (!value) return;
    try {
      await addTerm.mutateAsync(value);
      setNewTerm("");
      showSuccessToast(`"${value}" er tilføjet til godkendte ord.`);
    } catch (error) {
      logger.error("Failed to add PII whitelist term", {
        operation: "addPiiWhitelistTerm",
        error,
      });
      showErrorToast("Kunne ikke tilføje ordet. Måske findes det allerede.");
    }
  };

  const handleRemove = async (normalized: string, original: string) => {
    try {
      await removeTerm.mutateAsync(normalized);
      showSuccessToast(`"${original}" er fjernet.`);
    } catch (error) {
      logger.error("Failed to remove PII whitelist term", {
        operation: "removePiiWhitelistTerm",
        error,
      });
      showErrorToast("Kunne ikke fjerne ordet.");
    }
  };

  return (
    <PageContent title="Godkendte ord">
      <div className="flex h-full w-full flex-col space-y-2 p-4 xl:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">
              Godkendte ord
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Ord og termer her anonymiseres aldrig i chatten. Bemærk:
              CPR-numre, telefonnumre, e-mailadresser og kontonumre kan godt
              oprettes i listen herunder, men grundet disse oplysningers natur
              vil de altid blive markeret og fremhævet som identificerbare
              oplysninger i chats, hvorefter brugeren selv skal tage stilling
              til anonymiseringen heraf.
            </p>
          </div>
        </div>

        <form
          className="flex max-w-xl gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void handleAdd();
          }}
        >
          <Input
            value={newTerm}
            onChange={(e) => setNewTerm(e.target.value)}
            placeholder="Tilføj et ord eller en term…"
            aria-label="Nyt godkendt ord"
          />
          <Button type="submit" disabled={!newTerm.trim() || addTerm.isPending}>
            <Plus className="mr-2 h-4 w-4" />
            Tilføj
          </Button>
        </form>

        {whitelistQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : terms.length === 0 ? (
          <p className="py-8 text-sm text-muted-foreground">
            Ingen godkendte ord endnu.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ord</TableHead>
                <TableHead className="text-right">Handlinger</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {terms.map((term) => (
                <TableRow key={term.normalized}>
                  <TableCell className="text-foreground">
                    {term.original}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        void handleRemove(term.normalized, term.original)
                      }
                      disabled={removeTerm.isPending}
                      aria-label={`Fjern ${term.original}`}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </PageContent>
  );
};

export default PiiWhitelistSettingsPage;
